/**
 * Link Existing Customer Modal
 *
 * Opened from a company's Sites tab. Lets the user search across all existing
 * customers and attach one (or more) as a linked site under this company.
 * Filters out:
 *  - the company itself
 *  - other companies (a company can't be a child of another company)
 *  - customers already linked to THIS company (already a site)
 *
 * Customers currently linked to a DIFFERENT company are shown with a warning
 * pill so the user can knowingly re-parent them.
 */

import { useState, useMemo } from 'react';
import { X, Search, Building2, Link2, User, Check, Loader2, AlertTriangle } from 'lucide-react';
import { useModalAccessibility } from '@/hooks/useModalAccessibility';
import { useCustomers } from '@/hooks/useCustomers';
import { makeCustomerMatcher } from '@/utils/customer-search';
import { linkCustomerToCompany } from '@/services/customer.service';
import { Customer } from '@/types';
import { PhoneNumber } from './PhoneNumber';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  company: Customer | null;
  onLinked?: (linkedCustomer: Customer) => void;
}

export function LinkExistingCustomerModal({ isOpen, onClose, company, onLinked }: Props) {
  const { customers } = useCustomers();
  const [query, setQuery] = useState('');
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set());

  const modalRef = useModalAccessibility({ isOpen, onClose });

  // Candidates = everything that could plausibly become a site under this company
  const candidates = useMemo(() => {
    if (!company?.id) return [];
    // Shared matcher → searches names, company, every phone/address, account +
    // external IDs (same fields as global search).
    const hasQuery = query.trim().length > 0;
    const match = hasQuery ? makeCustomerMatcher(query) : null;

    return customers
      .filter((c) => {
        if (!c.id) return false;
        if (c.id === company.id) return false;            // not itself
        if (c.isCompany) return false;                     // companies can't be sites
        if (c.mergedInto) return false;                    // merged stubs are redirects, not linkable
        if (c.parentCompanyId === company.id) return false; // already linked here
        if (linkedIds.has(c.id)) return false;            // just linked in this session

        if (!match) return true;
        return match(c);
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .slice(0, 50); // cap long lists
  }, [customers, company?.id, query, linkedIds]);

  const handleLink = async (candidate: Customer) => {
    if (!candidate.id || !company?.id) return;

    const alreadyLinked = !!candidate.parentCompanyId && candidate.parentCompanyId !== company.id;
    if (alreadyLinked) {
      const ok = confirm(
        `${candidate.name} is currently linked to "${candidate.parentCompanyName || 'another company'}". ` +
          `Re-parent them to "${company.companyName || company.name}"?`,
      );
      if (!ok) return;
    }

    setLinkingId(candidate.id);
    try {
      await linkCustomerToCompany(candidate.id, company.id);
      setLinkedIds((prev) => {
        const next = new Set(prev);
        next.add(candidate.id!);
        return next;
      });
      onLinked?.({ ...candidate, parentCompanyId: company.id, parentCompanyName: company.companyName || company.name });
    } catch (err) {
      console.error('Failed to link customer:', err);
      alert('Failed to link customer. Please try again.');
    } finally {
      setLinkingId(null);
    }
  };

  if (!isOpen || !company) return null;

  const companyDisplayName = company.companyName || company.name;

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[210] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Link Existing Customer"
    >
      <div
        ref={modalRef}
        className="bg-gradient-to-br from-brand-primary to-brand-dark border border-brand-secondary/30 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand-secondary/20 to-transparent backdrop-blur-sm border-b border-white/10 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Link Existing Customer</h2>
              <p className="text-xs text-slate-400 flex items-center gap-1.5">
                <Building2 className="w-3 h-3" />
                Attach as a site under <span className="font-semibold text-slate-200">{companyDisplayName}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, account number, email, phone, address..."
              autoFocus
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary/50 text-sm"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Showing eligible customers (individuals not already linked here). Companies themselves are hidden.
          </p>
        </div>

        {/* Candidate list */}
        <div className="flex-1 overflow-y-auto p-4">
          {candidates.length === 0 ? (
            <div className="text-center py-12">
              <User className="w-10 h-10 mx-auto mb-3 text-slate-500 opacity-60" />
              <p className="text-slate-300 text-sm mb-1">
                {query.trim() ? 'No matches' : 'No customers available to link'}
              </p>
              <p className="text-xs text-slate-500">
                {query.trim()
                  ? 'Try a different search term'
                  : 'All customers are either companies or already linked here.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {candidates.map((c) => {
                const alreadyLinked = !!c.parentCompanyId;
                const isLinking = linkingId === c.id;
                return (
                  <div
                    key={c.id}
                    className="flex items-center justify-between gap-3 p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-white font-semibold text-sm truncate">{c.name}</h4>
                        {c.accountNumber && (
                          <span className="font-mono text-[10px] text-slate-400">{c.accountNumber}</span>
                        )}
                        {alreadyLinked && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-full text-[9px] font-bold uppercase">
                            <AlertTriangle className="w-2.5 h-2.5" />
                            Linked to {c.parentCompanyName || 'another'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-0.5">
                        {c.email && <span className="truncate">{c.email}</span>}
                        {c.phone && <PhoneNumber phone={c.phone} hideCopyUntilHover />}
                        {c.address && <span className="truncate">{c.address}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleLink(c)}
                      disabled={isLinking || linkingId !== null}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex-shrink-0 ${
                        alreadyLinked
                          ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30'
                          : 'bg-brand-secondary/20 hover:bg-brand-secondary/30 text-brand-secondary border border-brand-secondary/30'
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      {isLinking ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Link2 className="w-3.5 h-3.5" />
                      )}
                      {alreadyLinked ? 'Re-link' : 'Link'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 px-5 py-3 bg-black/20 border-t border-white/10 flex items-center justify-between">
          <div className="text-xs text-slate-400">
            {linkedIds.size > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-400">
                <Check className="w-3.5 h-3.5" />
                {linkedIds.size} customer{linkedIds.size === 1 ? '' : 's'} linked this session
              </span>
            ) : (
              <span>Pick one or more customers to attach</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
